import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaTrash, FaUpload, FaInfoCircle, FaCheck, FaTimesCircle } from "react-icons/fa";
import { MdPending } from "react-icons/md";
import { IoAddCircleOutline } from "react-icons/io5";
import Sidebar from "../Sidebar/Sidebar";
import { SmsApis } from "../../apis/smsApis";
import AdminNavbar from "../Navbars/AdminNavbar.js";

function NativeModal({ open, onClose, title, children, width = 'max-w-xl' }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; width?: string;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${width} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  approved: 'bg-green-100 text-green-800',
  pending:  'bg-yellow-100 text-yellow-800',
  rejected: 'bg-red-100 text-red-800',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  approved: <FaCheck className="inline mr-1" />,
  pending:  <MdPending className="inline mr-1" />,
  rejected: <FaTimesCircle className="inline mr-1" />,
};

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${STATUS_COLORS[s] ?? 'bg-gray-100 text-gray-800'}`}>
      {STATUS_ICONS[s]}{status}
    </span>
  );
}

function MessageTypeBadge({ type }: { type: string }) {
  return type === 'transactional'
    ? <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">Transactional</span>
    : <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">Promotional</span>;
}

export default function SenderIDManagement() {
  const [senderIDs, setSenderIDs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const perPage = 10;

  const [createOpen, setCreateOpen] = useState(false);
  const [newSenderID, setNewSenderID] = useState("");
  const [messageType, setMessageType] = useState("");
  const [purpose, setPurpose] = useState("");
  const [registrationOption, setRegistrationOption] = useState("");
  const [confirmGupta, setConfirmGupta] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedSenderID, setSelectedSenderID] = useState<any>(null);
  const [cacFile, setCacFile] = useState<File | null>(null);
  const [financialFile, setFinancialFile] = useState<File | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => { fetchSenderIDs(); }, [currentPage, statusFilter]);

  const fetchSenderIDs = async () => {
    setLoading(true);
    try {
      const res = await SmsApis.getSenderIds(perPage, statusFilter);
      if (res?.data) {
        setSenderIDs(res.data.data);
        setTotalPages(Math.ceil(res.data.total / res.data.per_page));
      }
    } catch { toast.error("Failed to fetch sender IDs"); }
    finally { setLoading(false); }
  };

  const resetCreate = () => {
    setNewSenderID(""); setMessageType(""); setPurpose("");
    setRegistrationOption(""); setConfirmGupta(false);
  };

  const openCreate = () => { resetCreate(); setCreateOpen(true); };

  const createSenderID = async (e: React.FormEvent) => {
    e.preventDefault();
    let senderIdToSubmit = newSenderID;
    if (messageType === 'transactional' && registrationOption === 'useGupta') {
      senderIdToSubmit = 'gupta';
    }
    if (messageType === 'promotional' || registrationOption === 'customSender') {
      if (!newSenderID || newSenderID.length < 3 || newSenderID.length > 11) {
        toast.error("Sender ID must be 3–11 characters"); return;
      }
    }
    try {
      const res = await SmsApis.createSenderId({
        sender_id: senderIdToSubmit,
        message_type: messageType,
        purpose,
        registration_option: messageType === 'transactional' ? registrationOption : 'standard',
      });
      if (res?.data) {
        toast.success("Sender ID request created. Please submit your documents using the 'Submit Docs' button.");
        fetchSenderIDs();
        setCreateOpen(false);
      } else {
        toast.error("Failed to create sender ID");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to create sender ID");
    }
  };

  const openUploadModal = (sid: any) => {
    setSelectedSenderID(sid);
    setCacFile(null);
    setFinancialFile(null);
    setWebsiteUrl(sid.documents?.website_url ?? "");
    setUploadOpen(true);
  };

  const uploadDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cacFile && !financialFile && !websiteUrl.trim()) {
      toast.error("Please provide at least one document or website URL");
      return;
    }
    setUploading(true);
    try {
      const res = await SmsApis.uploadSenderIdDocument(selectedSenderID.id, {
        cacCertificate: cacFile,
        financialLicense: financialFile,
        websiteUrl: websiteUrl.trim() || undefined,
      });
      if (res?.data) {
        toast.success("Documents submitted successfully");
        fetchSenderIDs();
        setUploadOpen(false);
      } else {
        toast.error("Failed to submit documents");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to submit documents");
    } finally {
      setUploading(false);
    }
  };

  const deleteSenderID = async () => {
    try {
      const res = await SmsApis.deleteSenderId(selectedSenderID.id);
      if (res?.data) { toast.success("Sender ID deleted"); fetchSenderIDs(); setDeleteOpen(false); }
      else toast.error("Failed to delete sender ID");
    } catch { toast.error("Failed to delete sender ID"); }
  };

  const checkStatus = async (id: number) => {
    try {
      const res = await SmsApis.checkSenderIdStatus(id);
      if (res?.data) { toast.info(`Status: ${res.data.status}`); fetchSenderIDs(); }
      else toast.error("Failed to check status");
    } catch { toast.error("Failed to check status"); }
  };

  return (
    <>
      <Sidebar title="Sender IDs" />
      <div className="relative md:ml-64 bg-gray-50 min-h-screen">
        <AdminNavbar title="" />
        <div className="px-4 md:px-10 mx-auto w-full py-8">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Sender IDs</h1>
              <p className="text-sm text-gray-500 mt-1">Manage your SMS sender identities</p>
            </div>
            <button
              onClick={openCreate}
              className="bg-[#0071BC] text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
            >
              <IoAddCircleOutline size={18} /> New Sender ID
            </button>
          </div>

          {/* Info banner */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6 flex gap-3">
            <FaInfoCircle className="text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              <strong>Quick Start:</strong> Use "gupta" for transactional messages (OTPs, alerts) with just your CAC certificate.
              For promotional messages or custom sender IDs, additional documentation may be required.
            </p>
          </div>

          {/* Filter */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <select
              className="border border-gray-300 text-gray-600 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="text-xs uppercase bg-gray-50 text-gray-500 border-b">
                  <tr>
                    <th className="px-6 py-4">Sender ID</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Purpose</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Created</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-16 text-center text-gray-400">
                        <div className="flex justify-center items-center gap-2">
                          <div className="w-5 h-5 border-2 border-[#0071BC] border-t-transparent rounded-full animate-spin" />
                          Loading…
                        </div>
                      </td>
                    </tr>
                  ) : senderIDs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-16 text-center text-gray-400">
                        <p className="font-medium">No sender IDs found</p>
                        <p className="text-xs mt-1">Create your first one using the "New Sender ID" button</p>
                      </td>
                    </tr>
                  ) : senderIDs.map(sid => (
                    <tr key={sid.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-800">{sid.sender_id}</div>
                        {sid.external_id && <div className="text-xs text-gray-400">ID: {sid.external_id}</div>}
                      </td>
                      <td className="px-6 py-4"><MessageTypeBadge type={sid.message_type || 'promotional'} /></td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs truncate text-gray-600" title={sid.purpose}>{sid.purpose || '—'}</div>
                      </td>
                      <td className="px-6 py-4"><StatusBadge status={sid.status} /></td>
                      <td className="px-6 py-4">{new Date(sid.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {(sid.status === 'pending' || sid.status === 'rejected') && (
                            <button
                              onClick={() => openUploadModal(sid)}
                              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1.5 whitespace-nowrap"
                            >
                              <FaUpload size={11} />
                              {sid.documents && Object.keys(sid.documents).length > 0 ? 'Update Docs' : 'Submit Docs'}
                            </button>
                          )}
                          <button
                            onClick={() => { setSelectedSenderID(sid); setDeleteOpen(true); }}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1.5"
                          >
                            <FaTrash size={11} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && senderIDs.length > 0 && totalPages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between text-sm text-gray-600">
                <span>Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed">
                    ‹ Prev
                  </button>
                  <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed">
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Sender ID Modal */}
      <NativeModal open={createOpen} onClose={() => setCreateOpen(false)} title="Create New Sender ID" width="max-w-2xl">
        <form onSubmit={createSenderID} className="space-y-5">
          {/* Message Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Message Type <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              {[
                { value: 'transactional', label: 'Transactional Messages', desc: 'OTPs, financial notifications, account alerts. Can bypass DND numbers.' },
                { value: 'promotional',   label: 'Promotional Messages',   desc: 'Marketing messages, announcements. Cannot contain numbers.' },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="messageType" value={opt.value} checked={messageType === opt.value}
                    onChange={e => setMessageType(e.target.value)} className="mt-0.5" required />
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Purpose */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">What will you be sending? <span className="text-red-500">*</span></label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
              placeholder="e.g., Account verification OTPs, Transaction alerts, Marketing promotions"
              value={purpose} onChange={e => setPurpose(e.target.value)} rows={3} required
            />
          </div>

          {/* Network availability */}
          {messageType && (
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-600 mb-2">Network Availability</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ MTN</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ GLO</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ 9MOBILE</span>
                {messageType === 'transactional'
                  ? <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">⚠ AIRTEL*</span>
                  : <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ AIRTEL</span>
                }
              </div>
              {messageType === 'transactional' && (
                <p className="text-xs text-gray-500 mt-1">*AIRTEL requires valid financial operating license</p>
              )}
            </div>
          )}

          {/* Registration option (transactional only) */}
          {messageType === 'transactional' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Registration Option <span className="text-red-500">*</span></label>
              <div className="space-y-2">
                {[
                  { value: 'useGupta',     label: 'Use "gupta" (Recommended)', desc: 'Only CAC certificate required. Quick approval.' },
                  { value: 'customSender', label: 'Custom Sender ID',           desc: 'Full documentation required. Longer approval.' },
                ].map(opt => (
                  <label key={opt.value} className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input type="radio" name="registrationOption" value={opt.value} checked={registrationOption === opt.value}
                      onChange={e => setRegistrationOption(e.target.value)} className="mt-0.5" required />
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Custom sender ID input */}
          {(messageType === 'promotional' || registrationOption === 'customSender') && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Sender ID <span className="text-red-500">*</span></label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
                placeholder="e.g., COMPANY, BRAND"
                value={newSenderID} onChange={e => setNewSenderID(e.target.value)} required
              />
              <p className="text-xs text-gray-400 mt-1">3–11 characters, alphanumeric only.</p>
            </div>
          )}

          {/* Gupta confirmation */}
          {messageType === 'transactional' && registrationOption === 'useGupta' && (
            <div className="bg-green-50 rounded-lg p-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={confirmGupta} onChange={e => setConfirmGupta(e.target.checked)} required />
                <span className="text-sm font-medium text-gray-800">I confirm using "gupta" as my sender ID</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">Your messages will appear from "gupta". Only your CAC certificate is required.</p>
            </div>
          )}

          {/* Documents required */}
          {messageType && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 text-sm mb-3">Required Documents</h4>
              {messageType === 'transactional' && registrationOption === 'useGupta' ? (
                <p className="text-sm text-gray-700">• CAC Certificate</p>
              ) : messageType === 'transactional' ? (
                <div className="space-y-1 text-sm text-gray-700">
                  <p>• CAC Certificate</p>
                  <p>• Website URL</p>
                  <p>• Financial operating license (for AIRTEL)</p>
                </div>
              ) : (
                <p className="text-sm text-gray-700">• CAC Certificate (recommended)</p>
              )}
              <div className="mt-3 bg-white rounded p-3 border-l-4 border-blue-500">
                <p className="text-sm font-semibold text-gray-800">📤 Document Submission</p>
                <p className="text-xs text-gray-500 mt-0.5">After creating, click <strong>"Submit Docs"</strong> on your sender ID to upload these documents directly.</p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setCreateOpen(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit"
              disabled={messageType === 'transactional' && registrationOption === 'useGupta' && !confirmGupta}
              className="flex-1 px-4 py-2.5 bg-[#0071BC] text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {messageType === 'promotional' ? 'Create Sender ID' : 'Submit Request'}
            </button>
          </div>
        </form>
      </NativeModal>

      {/* Submit Documents Modal */}
      <NativeModal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Submit Verification Documents" width="max-w-lg">
        <form onSubmit={uploadDocument} className="space-y-5">
          <p className="text-sm text-gray-500">
            Sender ID: <strong className="text-gray-800">{selectedSenderID?.sender_id}</strong>
          </p>

          {/* Already submitted docs */}
          {selectedSenderID?.documents && Object.keys(selectedSenderID.documents).length > 0 && (
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs font-semibold text-green-700 mb-2">Already submitted:</p>
              <ul className="space-y-1 text-xs text-green-800">
                {selectedSenderID.documents.cac_certificate && (
                  <li>• CAC Certificate —{' '}
                    <a href={selectedSenderID.documents.cac_certificate} target="_blank" rel="noreferrer"
                      className="underline hover:text-green-600">View</a>
                  </li>
                )}
                {selectedSenderID.documents.financial_license && (
                  <li>• Financial License —{' '}
                    <a href={selectedSenderID.documents.financial_license} target="_blank" rel="noreferrer"
                      className="underline hover:text-green-600">View</a>
                  </li>
                )}
                {selectedSenderID.documents.website_url && (
                  <li>• Website URL — <span className="font-mono">{selectedSenderID.documents.website_url}</span></li>
                )}
              </ul>
              <p className="text-xs text-green-600 mt-1">Upload new files below to replace them.</p>
            </div>
          )}

          {/* CAC Certificate */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              CAC Certificate <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={e => setCacFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-600 border border-gray-300 rounded-lg bg-gray-50 p-2 cursor-pointer"
            />
            <p className="text-xs text-gray-400 mt-1">PDF, JPG, or PNG · max 5 MB</p>
          </div>

          {/* Website URL */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Website URL</label>
            <input
              type="url"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              placeholder="https://yourwebsite.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Financial License (transactional custom only) */}
          {selectedSenderID?.message_type === 'transactional' && selectedSenderID?.registration_option === 'customSender' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Financial Operating License <span className="text-xs font-normal text-gray-400">(required for AIRTEL)</span>
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setFinancialFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-600 border border-gray-300 rounded-lg bg-gray-50 p-2 cursor-pointer"
              />
              <p className="text-xs text-gray-400 mt-1">PDF, JPG, or PNG · max 5 MB</p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setUploadOpen(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={uploading}
              className="flex-1 px-4 py-2.5 bg-[#0071BC] text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
              {uploading ? 'Submitting…' : 'Submit Documents'}
            </button>
          </div>
        </form>
      </NativeModal>

      {/* Delete Confirm Modal */}
      <NativeModal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Confirm Deletion" width="max-w-sm">
        <p className="text-sm text-gray-600 mb-6">
          Delete sender ID <strong>{selectedSenderID?.sender_id}</strong>? This cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={() => setDeleteOpen(false)}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={deleteSenderID}
            className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium">
            Delete
          </button>
        </div>
      </NativeModal>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}
